import { useCallback, useMemo, useState } from "react";
import { Button, Flex, Text } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import { Integration } from "@/api/integration/Integration.model";
import { useUpdateIntegrationMetadata } from "@/api/integration/Integration.api";
import Switch from "@/components/shared/inputs/switch/Switch";
import { toast } from "sonner";

interface ConfigureAccessDialogProps {
  integration: Integration;
  onClose: () => void;
  onSave: () => void;
}

export function ConfigureAccessDialog({
  integration,
  onClose,
  onSave,
}: ConfigureAccessDialogProps) {
  const { mutateAsync: updateMetadata, isLoading } =
    useUpdateIntegrationMetadata();

  const parsedMetadata = useMemo(() => {
    return integration.metadata ?? {};
  }, [integration.metadata]);

  const [allowFullAccess, setAllowFullAccess] = useState<boolean>(
    parsedMetadata.noAccessToRelease !== "true"
  );

  const handleSave = useCallback(async () => {
    try {
      const updatedMetadata = {
        ...parsedMetadata,
        noAccessToRelease: allowFullAccess ? "false" : "true",
      };

      await updateMetadata({
        type: integration.type,
        name: integration.name,
        metadata: updatedMetadata,
      });

      toast.success("Access configuration updated successfully");
      onSave();
    } catch (error) {
      toast.error("Failed to update access configuration");
    }
  }, [
    allowFullAccess,
    integration.type,
    integration.name,
    parsedMetadata,
    updateMetadata,
    onSave,
  ]);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <Dialog.Content size="small">
        <Dialog.Title>
          Configure Access
          <Dialog.Close />
        </Dialog.Title>

        <Flex direction="column" gap="4" p="4">
          <Text size="2" mb="4" color="gray">
            Configure access settings for AI agents to new articles.
          </Text>

          <Flex direction="column" gap="4" mb="4">
            <Flex align="center" justify="between" gap="3">
              <Text size="2" as="label">
                Allow AI agents full access to new articles
              </Text>
              <Switch
                value={allowFullAccess}
                onChange={setAllowFullAccess}
                disabled={isLoading}
              />
            </Flex>
          </Flex>

          <Flex gap="3" justify="end">
            <Button
              variant="soft"
              color="gray"
              highContrast
              disabled={isLoading}
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              color="gray"
              variant="solid"
              highContrast
              onClick={handleSave}
              disabled={isLoading}
            >
              Save
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
